/**
 * ConstellationDataBuilder
 *
 * Transforms raw EA canister data into a ConstellationData structure
 * for the constellation visualizer. Fetches all EA entities in parallel,
 * builds typed nodes with division/department hierarchy, determines
 * cluster affinity for positioning, and converts EA relationships
 * into weighted edges.
 *
 * Sprint 43: Dynamic entity types (string instead of union), risk/control
 * nodes, and optional session-based filtering.
 *
 * Follows OOA/OOD Single Responsibility: this class is solely responsible
 * for the data transformation pipeline from canister → constellation model.
 */

import type {
    EAOrganization,
    EAApplicationEntity,
    EATechnicalService,
    EABusinessServiceEntity,
    EADataObject,
    EAHRRole,
    EARelationship,
    CanisterClientInterface,
} from '../canister-client';

// ---------------------------------------------------------------------------
// Exported Interfaces  (S43.1: entityType is now `string`)
// ---------------------------------------------------------------------------

export interface ConstellationNode {
    id: string;
    label: string;
    entityType: string;       // S43.1: was union, now dynamic string
    parentId?: string;
    clusterAffinity?: string;
    size?: number;
    metadata: Record<string, string>;
}

export interface ConstellationEdge {
    id: string;
    source: string;
    target: string;
    relationshipType: string;
    weight: number;
}

export interface ConstellationData {
    nodes: ConstellationNode[];
    edges: ConstellationEdge[];
    statistics: {
        totalHeadcount: number;
        totalApplications: number;
        totalInfrastructure: number;
        divisions: number;
    };
    sessionId?: string;  // S43.5: track which session this data came from
}

// ---------------------------------------------------------------------------
// Risk / Control types (S43.5)
// ---------------------------------------------------------------------------

interface RiskEntity {
    risk_id: string;
    name: string;
    description: string;
    category: string;
    severity: string;
    likelihood: string;
    owner: string;
    status: string;
    linked_controls: string[];
    linked_applications: string[];
    metadata: Record<string, string>;
}

interface ControlEntity {
    control_id: string;
    name: string;
    description: string;
    control_type: string;
    effectiveness: string;
    owner: string;
    linked_risks: string[];
    linked_applications: string[];
    metadata: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Relationship weight mapping
// ---------------------------------------------------------------------------

const RELATIONSHIP_WEIGHT_MAP: Record<string, number> = {
    'integrates_with': 5,
    'supports': 4,
    'owns': 6,
    'uses': 3,
    'depends_on': 7,
    'mitigates': 5,
    'linked_to': 3,
};

const DEFAULT_RELATIONSHIP_WEIGHT = 2;

// ---------------------------------------------------------------------------
// Domain-to-division alias mapping (lowercase keyword -> lowercase division)
// ---------------------------------------------------------------------------

type AliasEntry = { keywords: string[]; divisionHints: string[] };

const DOMAIN_ALIAS_TABLE: AliasEntry[] = [
    { keywords: ['banking', 'retail', 'branch'],         divisionHints: ['front office', 'retail'] },
    { keywords: ['cards', 'card'],                       divisionHints: ['cards'] },
    { keywords: ['lending', 'loans', 'loan', 'mortgage'], divisionHints: ['lending', 'loans'] },
    { keywords: ['technology', 'tech', 'it'],            divisionHints: ['technology'] },
    { keywords: ['risk', 'compliance', 'regulatory'],    divisionHints: ['risk'] },
    { keywords: ['operations', 'ops'],                   divisionHints: ['operations'] },
    { keywords: ['finance', 'treasury'],                 divisionHints: ['finance'] },
    { keywords: ['hr', 'human resources', 'people'],     divisionHints: ['human resources', 'hr'] },
    { keywords: ['marketing', 'digital'],                divisionHints: ['marketing'] },
];

// ---------------------------------------------------------------------------
// Helper functions (module-level, not exported)
// ---------------------------------------------------------------------------

function slugify(text: string): string {
    return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

/**
 * Attempts to match a domain string to one of the known division IDs.
 *
 * @param domain - The domain or category string from an EA entity
 * @param divisionNameToId - Map of lowercase division name -> slugified ID
 * @returns The matched division ID, or undefined if no match is found
 */
function fuzzyDivisionMatch(
    domain: string,
    divisionNameToId: Map<string, string>,
): string | undefined {
    const domainLower = domain.toLowerCase();

    // Direct containment check: does the domain include a division name or vice versa?
    for (const [divName, divId] of divisionNameToId) {
        if (domainLower.includes(divName) || divName.includes(domainLower)) {
            return divId;
        }
    }

    // Alias-based check
    for (const alias of DOMAIN_ALIAS_TABLE) {
        const domainMatches = alias.keywords.some(kw => domainLower.includes(kw));
        if (domainMatches) {
            for (const hint of alias.divisionHints) {
                for (const [divName, divId] of divisionNameToId) {
                    if (divName.includes(hint)) {
                        return divId;
                    }
                }
            }
        }
    }

    return undefined;
}

/**
 * S43.5: Filter entities by import session ID in their metadata.
 */
function matchesSession(metadata: Record<string, string> | undefined, sessionId: string): boolean {
    if (!metadata) return false;
    return metadata['_import_session_id'] === sessionId;
}

// ---------------------------------------------------------------------------
// ConstellationDataBuilder
// ---------------------------------------------------------------------------

export class ConstellationDataBuilder {
    constructor(private canisterClient: CanisterClientInterface) {}

    /**
     * Fetches all EA entities from the canister and assembles a
     * ConstellationData payload suitable for the visualizer.
     *
     * @param sessionId - Optional import session ID to filter by (S43.5)
     */
    async build(sessionId?: string): Promise<ConstellationData> {
        // 1. Fetch all EA entities in parallel (including risks/controls)
        const [
            organizations,
            applications,
            technicalServices,
            businessServices,
            dataObjects,
            hrRoles,
            relationships,
            risks,
            controls,
        ] = await Promise.all([
            this.canisterClient.listEAOrganizations(),
            this.canisterClient.listEAApplications(),
            this.canisterClient.listEATechnicalServices(),
            this.canisterClient.listEABusinessServices(),
            this.canisterClient.listEADataObjects(),
            this.canisterClient.listEAHRRoles(),
            this.canisterClient.listEARelationships(),
            this.fetchRisks(),
            this.fetchControls(),
        ]);

        // S43.5: Apply session filter if provided
        const filterOrgs = sessionId
            ? organizations.filter(o => matchesSession((o as any).metadata, sessionId))
            : organizations;
        const filterApps = sessionId
            ? applications.filter(a => matchesSession((a as any).metadata, sessionId))
            : applications;
        const filterTechSvcs = sessionId
            ? technicalServices.filter(t => matchesSession((t as any).metadata, sessionId))
            : technicalServices;
        const filterBizSvcs = sessionId
            ? businessServices.filter(b => matchesSession((b as any).metadata, sessionId))
            : businessServices;
        const filterDataObjs = sessionId
            ? dataObjects.filter(d => matchesSession((d as any).metadata, sessionId))
            : dataObjects;
        const filterHRRoles = sessionId
            ? hrRoles.filter(h => matchesSession((h as any).metadata, sessionId))
            : hrRoles;
        const filterRisks = sessionId
            ? risks.filter(r => matchesSession(r.metadata, sessionId))
            : risks;
        const filterControls = sessionId
            ? controls.filter(c => matchesSession(c.metadata, sessionId))
            : controls;
        const filterRels = sessionId
            ? relationships.filter(r => matchesSession((r as any).metadata, sessionId))
            : relationships;

        const nodes: ConstellationNode[] = [];

        // 2. Build division + department nodes from organization data
        const { divisionMap, departmentToDivisionId, divisionNameToId } =
            this.buildDivisionAndDepartmentNodes(filterOrgs, nodes);

        // 3. Build application nodes and collect an app-id -> affinity lookup
        const applicationAffinityMap = this.buildApplicationNodes(
            filterApps,
            divisionNameToId,
            nodes,
        );

        // 4. Build technical service nodes
        this.buildTechnicalServiceNodes(filterTechSvcs, applicationAffinityMap, nodes);

        // 5. Build business service nodes
        this.buildBusinessServiceNodes(filterBizSvcs, divisionNameToId, nodes);

        // 6. Build data object nodes
        this.buildDataObjectNodes(filterDataObjs, filterApps, applicationAffinityMap, nodes);

        // 7. Build HR role nodes
        this.buildHRRoleNodes(filterHRRoles, departmentToDivisionId, divisionNameToId, nodes);

        // 8. S43.5: Build risk nodes
        this.buildRiskNodes(filterRisks, divisionNameToId, nodes);

        // 9. S43.5: Build control nodes
        this.buildControlNodes(filterControls, nodes);

        // 10. Build edges from EA relationships
        const nodeIdSet = new Set(nodes.map(n => n.id));
        const edges = this.buildEdges(filterRels, nodeIdSet);

        // 11. S43.5: Build risk-control edges from linked_controls / linked_risks
        this.buildRiskControlEdges(filterRisks, filterControls, nodeIdSet, edges);

        // 12. Calculate statistics
        const totalHeadcount = filterOrgs.reduce((sum, org) => sum + org.headcount, 0);
        const statistics = {
            totalHeadcount,
            totalApplications: filterApps.length,
            totalInfrastructure: 0,
            divisions: divisionMap.size,
        };

        return { nodes, edges, statistics, sessionId };
    }

    // -----------------------------------------------------------------------
    // Risk / Control fetching (S43.5)
    // -----------------------------------------------------------------------

    private async fetchRisks(): Promise<RiskEntity[]> {
        try {
            const result = await this.canisterClient.callCanister<RiskEntity[]>(
                'vector_db', 'list_risks', []
            );
            return Array.isArray(result) ? result : [];
        } catch {
            return [];
        }
    }

    private async fetchControls(): Promise<ControlEntity[]> {
        try {
            const result = await this.canisterClient.callCanister<ControlEntity[]>(
                'vector_db', 'list_controls', []
            );
            return Array.isArray(result) ? result : [];
        } catch {
            return [];
        }
    }

    // -----------------------------------------------------------------------
    // Division & Department nodes
    // -----------------------------------------------------------------------

    private buildDivisionAndDepartmentNodes(
        organizations: EAOrganization[],
        nodes: ConstellationNode[],
    ): {
        divisionMap: Map<string, EAOrganization[]>;
        departmentToDivisionId: Map<string, string>;
        divisionNameToId: Map<string, string>;
    } {
        // Group organizations by division
        const divisionMap = new Map<string, EAOrganization[]>();
        for (const org of organizations) {
            const existing = divisionMap.get(org.division) ?? [];
            existing.push(org);
            divisionMap.set(org.division, existing);
        }

        // Lowercase division name -> slugified ID (for fuzzy matching)
        const divisionNameToId = new Map<string, string>();
        // Department name -> division ID (for HR role affinity)
        const departmentToDivisionId = new Map<string, string>();

        for (const [division, orgs] of divisionMap) {
            const divId = slugify(division);
            divisionNameToId.set(division.toLowerCase(), divId);

            // Aggregate division-level metrics
            const totalHeadcount = orgs.reduce((s, o) => s + o.headcount, 0);
            const uniqueDepartments = new Set(orgs.map(o => o.department));
            const locationTypes = new Set(orgs.map(o => o.location_type));

            // Division compound node
            nodes.push({
                id: divId,
                label: division,
                entityType: 'division',
                metadata: {
                    headcount: String(totalHeadcount),
                    departments: String(uniqueDepartments.size),
                    location_types: Array.from(locationTypes).join(', '),
                },
            });

            // Department child nodes (unique per division)
            const seenDepartments = new Set<string>();
            for (const org of orgs) {
                const deptKey = `${division}-${org.department}`;
                if (seenDepartments.has(deptKey)) {
                    continue;
                }
                seenDepartments.add(deptKey);

                const deptId = slugify(deptKey);
                departmentToDivisionId.set(org.department.toLowerCase(), divId);

                // Aggregate department-level metrics
                const deptOrgs = orgs.filter(o => o.department === org.department);
                const deptHeadcount = deptOrgs.reduce((s, o) => s + o.headcount, 0);
                const teamCount = new Set(deptOrgs.map(o => o.team)).size;

                nodes.push({
                    id: deptId,
                    label: org.department,
                    entityType: 'department',
                    parentId: divId,
                    metadata: {
                        headcount: String(deptHeadcount),
                        reports_to: org.reports_to,
                        teams: String(teamCount),
                    },
                });
            }
        }

        return { divisionMap, departmentToDivisionId, divisionNameToId };
    }

    // -----------------------------------------------------------------------
    // Application nodes
    // -----------------------------------------------------------------------

    private buildApplicationNodes(
        applications: EAApplicationEntity[],
        divisionNameToId: Map<string, string>,
        nodes: ConstellationNode[],
    ): Map<string, string | undefined> {
        const applicationAffinityMap = new Map<string, string | undefined>();

        for (const app of applications) {
            const affinity = fuzzyDivisionMatch(app.domain, divisionNameToId);
            applicationAffinityMap.set(app.app_id, affinity);

            nodes.push({
                id: app.app_id,
                label: app.name,
                entityType: 'application',
                clusterAffinity: affinity,
                metadata: {
                    vendor: app.vendor,
                    app_type: app.app_type,
                    domain: app.domain,
                    criticality: app.criticality,
                    users: String(app.users),
                    integration_method: app.integration_method,
                },
            });
        }

        return applicationAffinityMap;
    }

    // -----------------------------------------------------------------------
    // Technical service nodes
    // -----------------------------------------------------------------------

    private buildTechnicalServiceNodes(
        technicalServices: EATechnicalService[],
        applicationAffinityMap: Map<string, string | undefined>,
        nodes: ConstellationNode[],
    ): void {
        for (const ts of technicalServices) {
            // Inherit affinity from the parent application
            const affinity = applicationAffinityMap.get(ts.application);

            nodes.push({
                id: ts.service_id,
                label: ts.name,
                entityType: 'technical-service',
                clusterAffinity: affinity,
                metadata: {
                    service_type: ts.service_type,
                    api_interface: ts.api_interface,
                    availability_sla: ts.availability_sla,
                    owner: ts.owner,
                    application: ts.application,
                },
            });
        }
    }

    // -----------------------------------------------------------------------
    // Business service nodes
    // -----------------------------------------------------------------------

    private buildBusinessServiceNodes(
        businessServices: EABusinessServiceEntity[],
        divisionNameToId: Map<string, string>,
        nodes: ConstellationNode[],
    ): void {
        for (const bs of businessServices) {
            const affinity = fuzzyDivisionMatch(bs.service_domain, divisionNameToId);

            nodes.push({
                id: bs.service_id,
                label: bs.service_offering || bs.business_service,
                entityType: 'business-service',
                clusterAffinity: affinity,
                metadata: {
                    service_domain: bs.service_domain,
                    business_service: bs.business_service,
                    channel: bs.channel,
                    sla: bs.sla,
                    service_owner: bs.service_owner,
                    technical_service: bs.technical_service,
                },
            });
        }
    }

    // -----------------------------------------------------------------------
    // Data object nodes
    // -----------------------------------------------------------------------

    private buildDataObjectNodes(
        dataObjects: EADataObject[],
        applications: EAApplicationEntity[],
        applicationAffinityMap: Map<string, string | undefined>,
        nodes: ConstellationNode[],
    ): void {
        // Build a lookup from application name (lowercase) -> app_id
        const appNameToId = new Map<string, string>();
        for (const app of applications) {
            appNameToId.set(app.name.toLowerCase(), app.app_id);
        }

        for (const dobj of dataObjects) {
            // Match source_system to an application name
            let affinity: string | undefined;
            const matchedAppId = appNameToId.get(dobj.source_system.toLowerCase());
            if (matchedAppId !== undefined) {
                affinity = applicationAffinityMap.get(matchedAppId);
            }

            nodes.push({
                id: dobj.data_object_id,
                label: dobj.name,
                entityType: 'data',
                clusterAffinity: affinity,
                metadata: {
                    domain: dobj.domain,
                    classification: dobj.classification,
                    source_system: dobj.source_system,
                    owner: dobj.owner,
                    retention: dobj.retention,
                    key_attributes: dobj.key_attributes,
                },
            });
        }
    }

    // -----------------------------------------------------------------------
    // HR role nodes
    // -----------------------------------------------------------------------

    private buildHRRoleNodes(
        hrRoles: EAHRRole[],
        departmentToDivisionId: Map<string, string>,
        divisionNameToId: Map<string, string>,
        nodes: ConstellationNode[],
    ): void {
        for (const hr of hrRoles) {
            // Try direct division match first, then fall back to department lookup
            const affinity =
                divisionNameToId.get(hr.division.toLowerCase()) ??
                departmentToDivisionId.get(hr.department.toLowerCase());

            nodes.push({
                id: hr.role_id,
                label: hr.role,
                entityType: 'hr-role',
                clusterAffinity: affinity,
                metadata: {
                    division: hr.division,
                    department: hr.department,
                    grade: hr.grade,
                    headcount: String(hr.headcount),
                    location: hr.location,
                    key_skills: hr.key_skills,
                    systems_used: hr.systems_used,
                },
            });
        }
    }

    // -----------------------------------------------------------------------
    // Risk nodes (S43.5)
    // -----------------------------------------------------------------------

    private buildRiskNodes(
        risks: RiskEntity[],
        divisionNameToId: Map<string, string>,
        nodes: ConstellationNode[],
    ): void {
        for (const risk of risks) {
            const affinity = fuzzyDivisionMatch(risk.category, divisionNameToId);

            nodes.push({
                id: risk.risk_id,
                label: risk.name,
                entityType: 'risk',
                clusterAffinity: affinity,
                metadata: {
                    category: risk.category,
                    severity: risk.severity,
                    likelihood: risk.likelihood || '',
                    owner: risk.owner || '',
                    status: risk.status || '',
                },
            });
        }
    }

    // -----------------------------------------------------------------------
    // Control nodes (S43.5)
    // -----------------------------------------------------------------------

    private buildControlNodes(
        controls: ControlEntity[],
        nodes: ConstellationNode[],
    ): void {
        for (const ctrl of controls) {
            nodes.push({
                id: ctrl.control_id,
                label: ctrl.name,
                entityType: 'control',
                metadata: {
                    control_type: ctrl.control_type,
                    effectiveness: ctrl.effectiveness,
                    owner: ctrl.owner || '',
                },
            });
        }
    }

    // -----------------------------------------------------------------------
    // Edges
    // -----------------------------------------------------------------------

    private buildEdges(
        relationships: EARelationship[],
        nodeIdSet: Set<string>,
    ): ConstellationEdge[] {
        const edges: ConstellationEdge[] = [];

        for (const rel of relationships) {
            // Only create an edge when both endpoints exist in the node set
            if (!nodeIdSet.has(rel.source_id) || !nodeIdSet.has(rel.target_id)) {
                continue;
            }

            const weight =
                RELATIONSHIP_WEIGHT_MAP[rel.relationship_type] ?? DEFAULT_RELATIONSHIP_WEIGHT;

            edges.push({
                id: rel.relationship_id,
                source: rel.source_id,
                target: rel.target_id,
                relationshipType: rel.relationship_type,
                weight,
            });
        }

        return edges;
    }

    // -----------------------------------------------------------------------
    // Risk-Control edges (S43.5)
    // -----------------------------------------------------------------------

    private buildRiskControlEdges(
        risks: RiskEntity[],
        controls: ControlEntity[],
        nodeIdSet: Set<string>,
        edges: ConstellationEdge[],
    ): void {
        let edgeCounter = 0;

        // Risk -> Control edges from linked_controls
        for (const risk of risks) {
            if (!nodeIdSet.has(risk.risk_id)) continue;
            for (const controlId of (risk.linked_controls || [])) {
                if (nodeIdSet.has(controlId)) {
                    edges.push({
                        id: `rc-edge-${edgeCounter++}`,
                        source: risk.risk_id,
                        target: controlId,
                        relationshipType: 'mitigated_by',
                        weight: RELATIONSHIP_WEIGHT_MAP['mitigates'] ?? DEFAULT_RELATIONSHIP_WEIGHT,
                    });
                }
            }
        }

        // Control -> Risk edges from linked_risks (avoid duplicates)
        const existingPairs = new Set(
            edges.filter(e => e.relationshipType === 'mitigated_by')
                .map(e => `${e.target}-${e.source}`) // reverse to match control->risk
        );

        for (const ctrl of controls) {
            if (!nodeIdSet.has(ctrl.control_id)) continue;
            for (const riskId of (ctrl.linked_risks || [])) {
                const pairKey = `${ctrl.control_id}-${riskId}`;
                if (nodeIdSet.has(riskId) && !existingPairs.has(pairKey)) {
                    edges.push({
                        id: `cr-edge-${edgeCounter++}`,
                        source: ctrl.control_id,
                        target: riskId,
                        relationshipType: 'mitigates',
                        weight: RELATIONSHIP_WEIGHT_MAP['mitigates'] ?? DEFAULT_RELATIONSHIP_WEIGHT,
                    });
                }
            }
        }
    }
}
